import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useCreateParent } from '@/api/parents';

/*
 * Adds a parent-portal account attached to a specific client. POSTs to
 * /api/parents which proxies to admin-server's /api/internal/parents
 * (Cognito + admin DB) and then back to PMA via /api/internal/users.
 *
 * Inputs are minimal — first / last / email. Cognito sends the invite
 * email automatically on AdminCreateUser; the parent picks a password
 * via the hosted-UI password-reset flow on first sign-in.
 */

interface AddParentDialogProps {
    clientId:   string;
    clientName: string;
    open:       boolean;
    onOpenChange: (open: boolean) => void;
}

interface FormState {
    firstName: string;
    lastName:  string;
    email:     string;
}

const EMPTY_FORM: FormState = { firstName: '', lastName: '', email: '' };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function AddParentDialog({ clientId, clientName, open, onOpenChange }: AddParentDialogProps) {
    const createMut = useCreateParent();
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [error, setError] = useState<string | null>(null);

    // Reset on close so a re-open starts clean. (Not on open: the user
    // might cancel mid-typing and reopen — they'd lose their work.
    // Resetting on close matches the AssignStaffDialog pattern.)
    useEffect(() => {
        if (!open) {
            setForm(EMPTY_FORM);
            setError(null);
        }
    }, [open]);

    const trimmedFirst = form.firstName.trim();
    const trimmedLast  = form.lastName.trim();
    const trimmedEmail = form.email.trim();
    const formValid = trimmedFirst && trimmedLast && EMAIL_RE.test(trimmedEmail);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!formValid || createMut.isPending) return;
        setError(null);
        try {
            await createMut.mutateAsync({
                first_name: trimmedFirst,
                last_name:  trimmedLast,
                email:      trimmedEmail,
                client_id:  clientId,
            });
            onOpenChange(false);
        } catch (err) {
            if (err instanceof AxiosError) {
                setError(err.response?.data?.error || err.message || 'Could not add parent account.');
            } else if (err instanceof Error) {
                setError(err.message);
            } else {
                setError('Could not add parent account.');
            }
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Add parent account</DialogTitle>
                    <DialogDescription>
                        Creates a portal account for a parent or guardian of{' '}
                        <span className="font-medium text-text">{clientName || 'this client'}</span>.
                        They'll receive an email with a sign-in link.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-3 mt-3">
                    <div className="grid grid-cols-2 gap-3">
                        <FormField label="First name" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.firstName}
                                    onChange={(e) => setForm((s) => ({ ...s, firstName: e.target.value }))}
                                    autoComplete="given-name"
                                    required
                                />
                            )}
                        </FormField>
                        <FormField label="Last name" required>
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.lastName}
                                    onChange={(e) => setForm((s) => ({ ...s, lastName: e.target.value }))}
                                    autoComplete="family-name"
                                    required
                                />
                            )}
                        </FormField>
                    </div>
                    <FormField label="Email" required>
                        {(id) => (
                            <Input
                                id={id}
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))}
                                autoComplete="email"
                                required
                            />
                        )}
                    </FormField>

                    {error && <Alert variant="danger">{error}</Alert>}

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={createMut.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            disabled={!formValid || createMut.isPending}
                        >
                            {createMut.isPending ? 'Adding…' : 'Add parent'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
